import { Offer, SupportedCurrencies, VatSettings } from '@shared/types';
import { FunctionComponent, useMemo } from 'react';
import { renderPriceOrFree } from '../../../../../services/renderPriceOrFree';
import { VATLabel } from '../../../../VATLabel';
import styles from './styles.module.scss';
import { PublicCourse, PublicOccurrence } from '@cademy-package/sdk-component-types';
import { renderPrice } from '../../../../../services/renderPrice';

type OccurrencePriceProps = {
    course: PublicCourse;
    isBookable: boolean;
    hidePricing?: boolean;
    occurrence: PublicOccurrence;
    vatSettings?: VatSettings;
};

const priceLabelFromOffers = (offers: Array<Offer>, currency: SupportedCurrencies): string => {
    const minimum = Math.min(...offers.map((offer) => (offer.price ? offer.price : 0)));
    const maximum = Math.max(...offers.map((offer) => (offer.price ? offer.price : 0)));
    if (minimum === maximum) {
        return renderPriceOrFree(minimum, currency);
    }
    return `${renderPriceOrFree(minimum, currency)} - ${renderPriceOrFree(maximum, currency)}`;
};

export const OccurrencePrice: FunctionComponent<OccurrencePriceProps> = ({
    course,
    isBookable,
    hidePricing,
    occurrence,
    vatSettings,
}) => {
    const priceLabel = useMemo(() => {
        if (occurrence.offers) {
            return priceLabelFromOffers(occurrence.offers, course.currency);
        }
        if (course.type === 'on-request') {
            switch (course.pricing_option.type) {
                case 'free':
                    return 'Free';
                case 'price-on-enquiry':
                    return 'Price on Enquiry';
                case 'price-range':
                    return `${renderPriceOrFree(course.pricing_option.minimumPrice, course.currency)} - ${renderPrice(course.pricing_option.maximumPrice, course.currency)}`;
                case 'price-offers':
                    return priceLabelFromOffers(course.pricing_option.offers, course.currency);
            }
        }
        return priceLabelFromOffers(course.offers, course.currency);
    }, [course, occurrence]);

    if (hidePricing || !isBookable) {
        return <span></span>;
    }

    return (
        <div className={styles.coursePriceLabel}>
            <span role="textbox" aria-label="Occurrence price">
                {priceLabel}
            </span>
            <VATLabel course={course} priceLabel={priceLabel} vatSettings={vatSettings} />
        </div>
    );
};
